import React, { useState, useEffect, useRef } from "react";
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Voicemail,
  Clock,
  Send,
  User,
  MessageSquare,
  ArrowRight,
  Calendar,
  Timer,
  Mic,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { internalCommentsApi } from "../../services/api";

// ── Interfaces ────────────────────────────────────────────────────────

interface CallNote {
  _id: string;
  body: string;
  authorId: string;
  authorName: string | null;
  mentions: string[];
  createdAt: string;
}

interface CallDetailModalProps {
  open: boolean;
  onClose: () => void;
  callLog: any | null;
  onUpdateNotes?: (notes: string) => void;
  currentUserId?: string;
  currentUserName?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────

function formatCallDuration(secs: number): string {
  if (!secs || secs < 1) return "0s";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatNoteTime(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function renderNoteBody(body: string): React.ReactNode {
  const parts = body.split(/(@\w+)/g);
  return parts.map((part, i) =>
    part.startsWith("@") ? (
      <span key={i} className="font-semibold" style={{ color: "#198754" }}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function getInitial(name: string | null): string {
  if (!name) return "?";
  return name.trim().charAt(0).toUpperCase() || "?";
}

function formatPhoneDisplay(phone: string): string {
  if (!phone) return "Unknown";
  // Format +1XXXXXXXXXX as (XXX) XXX-XXXX
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

const statusConfig: Record<string, { bg: string; text: string; icon: any; label: string }> = {
  completed: { bg: "bg-emerald-50", text: "text-emerald-700", icon: Phone, label: "Completed" },
  missed: { bg: "bg-red-50", text: "text-red-600", icon: PhoneMissed, label: "Missed" },
  no_answer: { bg: "bg-red-50", text: "text-red-600", icon: PhoneMissed, label: "No Answer" },
  voicemail: { bg: "bg-amber-50", text: "text-amber-700", icon: Voicemail, label: "Voicemail" },
  failed: { bg: "bg-red-50", text: "text-red-600", icon: PhoneMissed, label: "Failed" },
  initiated: { bg: "bg-blue-50", text: "text-blue-600", icon: Phone, label: "Initiated" },
  ringing: { bg: "bg-blue-50", text: "text-blue-600", icon: Phone, label: "Ringing" },
  in_progress: { bg: "bg-blue-50", text: "text-blue-600", icon: Phone, label: "In Progress" },
  canceled: { bg: "bg-slate-50", text: "text-slate-600", icon: PhoneMissed, label: "Canceled" },
  busy: { bg: "bg-amber-50", text: "text-amber-700", icon: Phone, label: "Busy" },
};

// ── Component ─────────────────────────────────────────────────────────

const CallDetailModal: React.FC<CallDetailModalProps> = ({
  open,
  onClose,
  callLog,
  onUpdateNotes,
  currentUserId,
  currentUserName,
}) => {
  const [callNotes, setCallNotes] = useState<CallNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [composeText, setComposeText] = useState("");
  const [sending, setSending] = useState(false);
  const notesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !callLog?._id) {
      setCallNotes([]);
      return;
    }

    let cancelled = false;
    const fetchNotes = async () => {
      setNotesLoading(true);
      try {
        const res = await internalCommentsApi.list({
          entityType: "call_log",
          entityId: callLog._id,
        });
        if (!cancelled) {
          const comments = res.data?.data || res.data || [];
          setCallNotes(
            comments.map((c: any) => ({
              _id: c._id,
              body: c.body,
              authorId: typeof c.authorId === "object" ? c.authorId?._id : c.authorId,
              authorName: c.authorName || (typeof c.authorId === "object" ? c.authorId?.name : null),
              mentions: c.mentions || [],
              createdAt: c.createdAt,
            }))
          );
        }
      } catch {
        if (!cancelled) setCallNotes([]);
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    };

    fetchNotes();
    return () => { cancelled = true; };
  }, [open, callLog?._id]);

  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [callNotes]);

  if (!callLog) return null;

  const status = statusConfig[callLog.status] || {
    bg: "bg-slate-50",
    text: "text-slate-600",
    icon: Phone,
    label: callLog.status || "Unknown",
  };
  const StatusIcon = status.icon;
  const isInbound = callLog.direction === "inbound";
  const DirectionIcon = isInbound ? PhoneIncoming : PhoneOutgoing;

  const fromName = callLog.from?.displayName;
  const fromPhone = callLog.from?.phoneE164 || "";
  const toName = callLog.to?.displayName;
  const toPhone = callLog.to?.phoneE164 || "";

  // The "remote" party (the one the user called or received from)
  const remoteName = isInbound
    ? fromName || formatPhoneDisplay(fromPhone)
    : toName || formatPhoneDisplay(toPhone);
  const remotePhone = isInbound ? fromPhone : toPhone;

  const handleSendNote = async () => {
    const body = composeText.trim();
    if (!body || !callLog._id) return;

    const mentionMatches = body.match(/@(\w+)/g) || [];
    const mentions = mentionMatches.map((m) => m.slice(1));

    setSending(true);
    try {
      await internalCommentsApi.create({
        conversationId: callLog.conversationId,
        entityType: "call_log",
        entityId: callLog._id,
        body,
        mentions,
      });
      // Optimistic update
      const newNote: CallNote = {
        _id: `temp-${Date.now()}`,
        body,
        authorId: currentUserId || "",
        authorName: currentUserName || "You",
        mentions,
        createdAt: new Date().toISOString(),
      };
      setCallNotes((prev) => [...prev, newNote]);
      setComposeText("");
    } catch (err) {
      console.error("Failed to add call note:", err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendNote();
    }
  };

  return (
    <PremiumModal open={open} onClose={onClose} title="" size="md">
      <div className="-mt-6">
        {/* ── Hero Section ── */}
        <div className="text-center pb-5 border-b border-slate-100">
          {/* Icon circle */}
          <div className="relative inline-flex">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto"
              style={{
                background: callLog.status === "completed"
                  ? "linear-gradient(135deg, #059669, #10b981)"
                  : callLog.status === "missed" || callLog.status === "no_answer"
                  ? "linear-gradient(135deg, #dc2626, #ef4444)"
                  : callLog.status === "voicemail"
                  ? "linear-gradient(135deg, #d97706, #f59e0b)"
                  : "linear-gradient(135deg, #0F5132, #198754)",
              }}
            >
              <DirectionIcon className="h-7 w-7 text-white" />
            </div>
            {/* Status dot */}
            <div
              className={`absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full border-2 border-white flex items-center justify-center ${status.bg}`}
            >
              <StatusIcon className={`h-2.5 w-2.5 ${status.text}`} />
            </div>
          </div>

          {/* Contact name & number */}
          <h3 className="mt-3 text-lg font-bold text-slate-900">{remoteName}</h3>
          {remotePhone && remoteName !== formatPhoneDisplay(remotePhone) && (
            <p className="text-sm text-slate-500 mt-0.5">{formatPhoneDisplay(remotePhone)}</p>
          )}

          {/* Direction + Status badges */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${
                isInbound ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"
              }`}
            >
              <DirectionIcon className="h-3 w-3" />
              {isInbound ? "Inbound" : "Outbound"}
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${status.bg} ${status.text}`}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </span>
          </div>
        </div>

        {/* ── Call Flow Card ── */}
        <div className="mt-5 rounded-xl bg-slate-50/80 border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            {/* From */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">From</p>
              <p className="text-sm font-semibold text-slate-800 truncate">
                {fromName || formatPhoneDisplay(fromPhone)}
              </p>
              <p className="text-xs text-slate-500 truncate">{formatPhoneDisplay(fromPhone)}</p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-white border border-slate-200 shadow-sm">
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
            </div>

            {/* To */}
            <div className="flex-1 min-w-0 text-right">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">To</p>
              <p className="text-sm font-semibold text-slate-800 truncate">
                {toName || formatPhoneDisplay(toPhone)}
              </p>
              <p className="text-xs text-slate-500 truncate">{formatPhoneDisplay(toPhone)}</p>
            </div>
          </div>
        </div>

        {/* ── Time Details Row ── */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-slate-50/80 border border-slate-100 px-3 py-3 text-center">
            <Calendar className="h-4 w-4 text-slate-400 mx-auto mb-1.5" />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Started</p>
            <p className="text-sm font-medium text-slate-700 mt-1">
              {formatTime(callLog.startedAt)}
            </p>
            <p className="text-[10px] text-slate-400">
              {callLog.startedAt
                ? new Date(callLog.startedAt).toLocaleDateString([], { month: "short", day: "numeric" })
                : "—"}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50/80 border border-slate-100 px-3 py-3 text-center">
            <Timer className="h-4 w-4 text-slate-400 mx-auto mb-1.5" />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Duration</p>
            <p className="text-sm font-medium text-slate-700 mt-1">
              {callLog.durationSecs ? formatCallDuration(callLog.durationSecs) : "—"}
            </p>
            <p className="text-[10px] text-slate-400">
              {callLog.durationSecs ? `${callLog.durationSecs} seconds` : "N/A"}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50/80 border border-slate-100 px-3 py-3 text-center">
            <Clock className="h-4 w-4 text-slate-400 mx-auto mb-1.5" />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Ended</p>
            <p className="text-sm font-medium text-slate-700 mt-1">
              {formatTime(callLog.endedAt)}
            </p>
            <p className="text-[10px] text-slate-400">
              {callLog.endedAt
                ? new Date(callLog.endedAt).toLocaleDateString([], { month: "short", day: "numeric" })
                : "—"}
            </p>
          </div>
        </div>

        {/* ── Recording Player ── */}
        {callLog.recording?.url && (
          <div className="mt-4 rounded-xl bg-slate-50/80 border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="h-4 w-4 text-slate-500" />
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Recording
              </p>
              {callLog.recording.duration && (
                <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full font-medium ml-auto">
                  {formatCallDuration(callLog.recording.duration)}
                </span>
              )}
            </div>
            <audio
              controls
              src={callLog.recording.url}
              className="w-full h-10 rounded-lg"
              style={{ filter: "hue-rotate(120deg) saturate(0.5)" }}
            />
          </div>
        )}

        {/* ── Voicemail Transcription ── */}
        {callLog.voicemail?.transcription && (
          <div className="mt-4 rounded-xl bg-amber-50/60 border border-amber-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Voicemail className="h-4 w-4 text-amber-600" />
              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-widest">
                Voicemail Transcription
              </p>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed italic">
              &ldquo;{callLog.voicemail.transcription}&rdquo;
            </p>
          </div>
        )}

        {/* ── Notes Section ── */}
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="h-4 w-4 text-slate-400" />
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Notes
            </p>
            {callNotes.length > 0 && (
              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                {callNotes.length}
              </span>
            )}
          </div>

          {/* Notes list */}
          <div className="max-h-56 overflow-y-auto space-y-2.5 mb-3">
            {/* Legacy note */}
            {callLog.notes && (
              <div className="flex items-start gap-2.5">
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-slate-300 flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="flex-1 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-slate-500">Note</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {callLog.notes}
                  </p>
                </div>
              </div>
            )}

            {notesLoading && callNotes.length === 0 && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin h-4 w-4 border-2 border-slate-200 border-t-emerald-600 rounded-full" />
                <span className="ml-2 text-xs text-slate-400">Loading notes...</span>
              </div>
            )}

            {callNotes.map((note) => {
              const isCurrentUser = currentUserId && note.authorId === currentUserId;
              return (
                <div key={note._id} className="flex items-start gap-2.5">
                  <div
                    className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-sm"
                    style={{
                      backgroundColor: isCurrentUser ? "#198754" : "#0F5132",
                    }}
                  >
                    {getInitial(note.authorName)}
                  </div>
                  <div
                    className={`flex-1 rounded-xl px-3 py-2.5 border ${
                      isCurrentUser
                        ? "bg-emerald-50/60 border-emerald-100"
                        : "bg-slate-50 border-slate-100"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-slate-700">
                        {note.authorName || "Unknown"}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {formatNoteTime(note.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {renderNoteBody(note.body)}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Empty state */}
            {!notesLoading && callNotes.length === 0 && !callLog.notes && (
              <div className="flex flex-col items-center justify-center py-8 text-slate-300">
                <MessageSquare className="h-8 w-8 mb-2" />
                <p className="text-xs text-slate-400">No notes yet</p>
                <p className="text-[10px] text-slate-300 mt-0.5">Add a note to keep track of this call</p>
              </div>
            )}

            <div ref={notesEndRef} />
          </div>

          {/* Compose bar */}
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500/20 transition-all">
            <input
              type="text"
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a note..."
              className="flex-1 text-sm outline-none placeholder:text-slate-400 bg-transparent"
              disabled={sending}
            />
            <button
              onClick={handleSendNote}
              disabled={!composeText.trim() || sending}
              className="flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-white transition-all disabled:opacity-30 hover:shadow-md active:scale-95"
              style={{ backgroundColor: "#0F5132" }}
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </PremiumModal>
  );
};

export default CallDetailModal;
